import type { StudySelectDto } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { getStudyAdversaryProfileLabel, getStudyTimeHorizonLabel } from '@utils/meia-enum.util';
import { EditIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { MeiaKopModal } from './meia-kop-modal.component';

type MeiaKopTileProps = {
  canEdit: boolean;
  study: StudySelectDto;
};

export const MeiaKopTile: FC<MeiaKopTileProps> = (props) => {
  const { canEdit, study } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const adversaryProfiles = study.adversaryProfiles ?? [];
  const adversaryProfileItems = adversaryProfiles.map((adversaryProfile) => ({
    id: adversaryProfile,
    name: getStudyAdversaryProfileLabel(adversaryProfile),
  }));
  const hasProblem = Boolean(study.keyOperationalProblem?.trim());

  return (
    <>
      <Tile
        emptyState={{ title: 'No Key Operational Problem' }}
        header={{
          iconButton: canEdit
            ? {
                icon: <EditIcon size={14} />,
                onClick: () => setIsModalOpen(true),
              }
            : undefined,
          title: (
            <span className="inline-flex items-center gap-1.5">
              <span>Key Operational Problem</span>
              <TooltipComponent side="top">
                One Key Operational Problem can have many missions; each mission owns its own threads downstream.
              </TooltipComponent>
            </span>
          ),
        }}
        hoverReveal={canEdit}
        isEmpty={!hasProblem}
      >
        <div className="flex flex-col gap-3 text-sm">
          <section className="flex flex-col gap-1">
            <h4 className="font-medium text-gray-700">Problem Statement</h4>
            <p className="whitespace-pre-wrap leading-relaxed text-gray-600">{study.keyOperationalProblem}</p>
          </section>
          <div className="grid gap-2 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <h4 className="font-medium text-gray-700">Adversary Profile</h4>
              {adversaryProfiles.length > 0 ? (
                <BadgeList
                  badgeVariant="outline"
                  items={adversaryProfileItems}
                  limit={adversaryProfileItems.length}
                  size="xs"
                />
              ) : (
                <span className="text-gray-500">—</span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="font-medium text-gray-700">Time Horizon</h4>
              {study.timeHorizon ? (
                <Badge
                  className="w-fit"
                  size="xs"
                  variant="outline"
                >
                  {getStudyTimeHorizonLabel(study.timeHorizon)}
                </Badge>
              ) : (
                <span className="text-gray-500">—</span>
              )}
            </div>
          </div>
        </div>
      </Tile>

      {canEdit && (
        <MeiaKopModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          study={study}
        />
      )}
    </>
  );
};
